<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  offer: { type: Object, required: true },
  index: { type: Number, default: null },
});

const { t } = useI18n();

const vehicle = computed(() => props.offer.vehicle || {});

// Pickup-country equivalent (e.g. GBP for a UK pickup) shown beneath the charge
// price, only when the gateway returns a renting currency that differs.
const rentingEquiv = computed(() => {
  const { rentingAmount, rentingCurrency, currency } = props.offer;
  if (
    rentingAmount == null ||
    !rentingCurrency ||
    rentingCurrency === currency
  ) {
    return null;
  }
  return `≈ ${rentingAmount} ${rentingCurrency}`;
});
</script>

<template>
  <div class="flex w-full items-center gap-3">
    <div
      class="flex size-11 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-n-alpha-2 text-sm font-semibold text-n-slate-11"
    >
      <span v-if="index !== null">{{ index + 1 }}</span>
      <img
        v-else-if="vehicle.imageUrl"
        :src="vehicle.imageUrl"
        :alt="vehicle.sampleModel || offer.category"
        class="size-full object-contain"
      />
      <span v-else class="i-lucide-car size-5" />
    </div>

    <div class="flex min-w-0 flex-1 flex-col gap-0.5">
      <span class="truncate text-sm font-medium text-n-slate-12">
        {{ vehicle.sampleModel || offer.category }}
      </span>
      <span class="truncate text-xs text-n-slate-11">
        <span>{{ offer.category }}</span>
        <span class="before:mx-1 before:content-['·']">{{
          offer.provider
        }}</span>
      </span>
      <div
        class="mt-0.5 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-xs text-n-slate-11"
      >
        <span v-if="vehicle.seats" class="inline-flex items-center gap-1">
          <span class="i-lucide-users size-3.5" />{{ vehicle.seats }}
        </span>
        <span v-if="vehicle.bags" class="inline-flex items-center gap-1">
          <span class="i-lucide-briefcase size-3.5" />{{ vehicle.bags }}
        </span>
        <span
          v-if="vehicle.transmission"
          class="inline-flex items-center gap-1"
        >
          <span class="i-lucide-settings-2 size-3.5" />
          <span class="capitalize">{{
            vehicle.transmission.toLowerCase()
          }}</span>
        </span>
        <span v-if="vehicle.airCon" class="inline-flex items-center gap-1">
          <span class="i-lucide-snowflake size-3.5" />{{
            t('CONVERSATION.CAR_BOOKING.OFFERS.AC')
          }}
        </span>
      </div>
    </div>

    <div class="flex shrink-0 flex-col items-end">
      <span class="text-sm font-semibold text-n-slate-12">
        {{ offer.amount }} {{ offer.currency }}
      </span>
      <span class="text-xs text-n-slate-11">
        {{ t('CONVERSATION.CAR_BOOKING.OFFERS.TOTAL') }}
      </span>
      <span v-if="rentingEquiv" class="mt-0.5 text-xs text-n-slate-10">
        {{ rentingEquiv }} {{ t('CONVERSATION.CAR_BOOKING.OFFERS.AT_PICKUP') }}
      </span>
    </div>
  </div>
</template>
